import { useQuery, useQueries } from '@tanstack/react-query';
import { useApi } from 'common/hooks/api';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays } from 'date-fns';

export const useRenderDataItinerary = id => {
  const Api = useApi();
  return useQuery({
    queryKey: ['renderDataItinerary', id],
    queryFn: async () => {
      const response = await Api.get(`/api/PresentationEdits/renderDataItinerary?itineraryId=${id}&includeAllImages=1`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
};

export const useRenderDataSavedItinerary = (id, { agencyId, agentId } = {}) => {
  const Api = useApi();
  return useQuery({
    queryKey: ['renderDataSavedItinerary', id, agencyId, agentId],
    queryFn: async () => {
      const params = new URLSearchParams({ savedItineraryId: id });
      if (agencyId) params.append('agencyId', agencyId);
      if (agentId) params.append('agentId', agentId);
      const response = await Api.get(`/api/PresentationEdits/renderDataSavedItinerary?${params}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
};

export const useShareLink = (clientId, itineraryId) => {
  const Api = useApi();
  return useQuery({
    queryKey: ['copy-share-link', `itinerary-${itineraryId}`],
    queryFn: async () => {
      const response = await Api.createShareLink(clientId, itineraryId);
      return response.data;
    },
    enabled: !!clientId && !!itineraryId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useItineraryPoll = (itineraryId, { enabled = false, refetchInterval = false, onError } = {}) => {
  const Api = useApi();
  return useQuery({
    queryKey: ['itinerary', itineraryId],
    queryFn: () => Api.getItinerary(itineraryId).then(r => r.data),
    enabled: !!itineraryId && enabled,
    refetchInterval,
    refetchIntervalInBackground: false,
    onError,
  });
};

export const useEdgeAvailability = (savedItineraryId, currentDate, { adults, childrenAges } = {}) => {
  const Api = useApi();
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const weeks = [];
  let cursor = calStart;
  while (cursor <= calEnd) {
    const wEnd = endOfWeek(cursor, { weekStartsOn: 1 });
    weeks.push({ start: format(cursor, 'yyyy-MM-dd'), end: format(wEnd, 'yyyy-MM-dd') });
    cursor = addDays(wEnd, 1);
  }

  const results = useQueries({
    queries: weeks.map(w => ({
      queryKey: ['edge-availability', savedItineraryId, w.start, w.end, adults, childrenAges],
      queryFn: async () => {
        const response = await Api.getSavedItineraryAvailability(savedItineraryId, w.start, w.end, [], false, adults, childrenAges);
        return response.data;
      },
      enabled: !!savedItineraryId,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = results.some(r => r.isLoading);
  const data = { availability: {} };
  for (const r of results) {
    if (r.data?.availability) Object.assign(data.availability, r.data.availability);
    if (!data.accommodations && r.data?.accommodations) data.accommodations = r.data.accommodations;
  }

  return { data, isLoading };
};
